import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './EmpresasModal';
import EmpresasTabs from './EmpresasTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { FaArrowLeft } from 'react-icons/fa';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = 'Catálogo de Empresas';
const endpoint = ENDPOINTS.empresas;
const endpointById = ENDPOINTS.empresasById;
const modelo = Models.empresas;



export default function EmpresasCatalog() {
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState(null);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
  // Cache de nombres de empresas padre que NO están en el conjunto filtrado visible
  const [parentNamesExtra, setParentNamesExtra] = useState({});
  const [filterPadreId, setFilterPadreId] = useState(null);
  const [showChildData, setShowChildData] = useState(false);

  const userEmpresaId = getSessionEmpresaId();
  const isSuperAdmin = isSessionSuperAdmin();

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };
  const editorRef = useRef(null);

  // useEffect(() => {
  //   if (modalOpen && editorRef.current) {
  //     editorRef.current.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      const newRows = (res.data || []).map((x) => modelo(x));
      setRows(newRows);

      // Resolver nombres de empresas padre que no están en el conjunto filtrado
      const idsEnRows = new Set(newRows.map((r) => r.idEmpresa));
      const idsPadreExternos = [
        ...new Set(
          newRows
            .map((r) => r.idPadre)
            .filter((id) => id != null && !idsEnRows.has(id))
        ),
      ];

      if (idsPadreExternos.length > 0) {
        const resultados = await Promise.allSettled(
          idsPadreExternos.map((id) => axios.get(ENDPOINTS.empresasById(id)))
        );
        const nuevoCache = {};
        resultados.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value?.data) {
            const emp = modelo(r.value.data);
            nuevoCache[emp.idEmpresa] = emp.nombre || `Empresa #${idsPadreExternos[i]}`;
          }
        });
        setParentNamesExtra((prev) => ({ ...prev, ...nuevoCache }));
      }
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let result = [...rows];

    // 1. Filtro jerárquico manual (desde botón de fila)
    if (filterPadreId !== null) {
      result = result.filter(r => r.idEmpresa === filterPadreId || r.idPadre === filterPadreId);
    } 
    // 2. Filtro de visibilidad estándar (Multi-Tenant)
    else {
      result = result.filter(r => {
        if (isSuperAdmin) return true;

        // Es MI empresa (o la empresa raíz que me corresponde)
        const isOwn = r.idEmpresa === userEmpresaId;
        if (isOwn) return true;

        // Es una empresa HIJA: solo se ve si el botón global está activo
        const isHija = r.idPadre === userEmpresaId;
        return isHija && showChildData;
      });
    }

    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => {
        const nombre = (r.nombre || '').toLowerCase();
        const idPadre = r.idPadre == null ? '' : String(r.idPadre);
        return nombre.includes(q) || idPadre.toLowerCase().includes(q);
      });
    }

    // Ordenamiento: Empresa actual/padre primero, luego alfabético
    result.sort((a, b) => {
      // Si hay un filtro manual activo, ese padre va primero
      if (filterPadreId !== null) {
        if (a.idEmpresa === filterPadreId && b.idEmpresa !== filterPadreId) return -1;
        if (b.idEmpresa === filterPadreId && a.idEmpresa !== filterPadreId) return 1;
      }
      
      // La empresa del usuario va primero (si aplica)
      if (a.idEmpresa === userEmpresaId && b.idEmpresa !== userEmpresaId) return -1;
      if (b.idEmpresa === userEmpresaId && a.idEmpresa !== userEmpresaId) return 1;

      // Resto en orden alfabético
      return (a.nombre || '').localeCompare(b.nombre || '');
    });

    return result;
  }, [rows, query, filterPadreId, showChildData, isSuperAdmin, userEmpresaId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const parentNames = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      map[r.idEmpresa] = r.nombre || `Empresa #${r.idEmpresa}`;
    });
    return map;
  }, [rows]);

  const formatParent = (row) => {
    if (row.idPadre == null || row.idPadre === '') return 'Empresa Padre';
    // Busca primero en el mapa local (empresas visibles); si no, en el cache remoto
    const nombre = parentNames[row.idPadre] || parentNamesExtra[row.idPadre];
    return normalizeValue(nombre || `Empresa #${row.idPadre}`);
  };

  const handleEdit = (id) => {
    const r = rows.find((x) => x.idEmpresa === id);
    if (!r) {
      triggerAlert('No se encontró la empresa en memoria');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      console.log('DEBUG EMPRESAS CATALOG - Recibido de Modal:', payload);
      if (mode === 'new') {
        const res = await axios.post(endpoint, payload);
        const newId = res?.data?.idEmpresa ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else if (selectedId != null) {
        await axios.put(endpoint, { IdEmpresa: selectedId, ...payload });
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre o ID padre…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />

          {!isSuperAdmin && (
            <button
              className={`btn-action ${showChildData ? 'btn-action--active' : ''}`}
              style={{
                backgroundColor: showChildData ? '#0f172a' : '#f1f5f9',
                color: showChildData ? '#fff' : '#475569',
                border: '1px solid #cbd5e1'
              }}
              onClick={() => { setShowChildData(!showChildData); setFilterPadreId(null); setPage(1); }}
              title={showChildData ? "Ocultar Hijas" : "Ver Hijas"}
            >
              {showChildData ? '👁️ Ocultar Hijas' : '👁️ Ver Hijas'}
            </button>
          )}

          {filterPadreId !== null && (
            <button 
              className="btn-filter is-active" 
              onClick={() => { setFilterPadreId(null); setPage(1); }}
              title="Quitar filtro de jerarquía"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FaArrowLeft size={12} /> Ver Todas
            </button>
          )}

          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Empresa Padre</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idEmpresa} className={editingRowId === r.idEmpresa ? 'rk-row-active' : ''}>
                    <td><strong>{normalizeValue(r.nombre)}</strong></td>
                    <td>{formatParent(r)}</td>
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idEmpresa)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            askDelete(r.idEmpresa, r.nombre || 'Empresa');
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={2 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map((n) => (
              <PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />
            ))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            {modalMode === 'new' && (
              <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                <span>
                  Selecciona o crea una empresa y pulsa <b>Editar</b> para gestionar sus datos
                  relacionados.
                </span>
              </div>
            )}
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              loading={loading}
              parentOptions={rows}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
            {modalMode === 'edit' && editingRowId > 0 && (
              <EmpresasTabs
                empresaId={editingRowId}
                puedeCrear={puedeCrear}
                puedeEditar={puedeEditar}
                puedeEliminar={puedeEliminar}
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
